/* eslint-disable no-unused-vars, eqeqeq */
import { useState, useEffect, Fragment, useRef } from "react";
import dayjs from "dayjs";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import DeleteIcon from "@mui/icons-material/Delete";
import Container from "@mui/material/Box";
import EditIcon from "@mui/icons-material/Edit";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import { jwtDecode } from "jwt-decode";

import {
  getRooms,
  getDevices,
  createDevice,
  addToRoom,
  deviceBlink,
  getDevicesFilter,
  deleteDevice,
} from "../service/service_handler";
import { isSensorTelemetryStale } from "../../utils/sensorTelemetryFreshness";

export default function DeviceScreen() {
  const LIGHTS_COL_WIDTH_KEY = "device_manager_lights_col_widths_v1";
  const SENSOR_COL_WIDTH_KEY = "device_manager_sensor_col_widths_v1";
  const MIN_COL_WIDTH = 90;

  const lightColumns = [
    { key: "deviceName", label: "Device Name", align: "left", defaultWidth: 180 },
    { key: "deviceId", label: "Device Id", align: "left", defaultWidth: 170 },
    { key: "deviceTemperature", label: "Device Temperature", align: "center", defaultWidth: 170 },
    { key: "fanSpeed", label: "Fan Speed", align: "center", defaultWidth: 130 },
    { key: "roomName", label: "Room Name", align: "left", defaultWidth: 160 },
    { key: "orgCode", label: "Organization", align: "left", defaultWidth: 160, adminOnly: true },
    { key: "deviceStatus", label: "Device Status", align: "center", defaultWidth: 150 },
    { key: "actions", label: "Actions", align: "center", defaultWidth: 280 },
  ];

  const sensorColumns = [
    { key: "deviceName", label: "Device Name", align: "left", defaultWidth: 180 },
    { key: "deviceId", label: "Device Id", align: "left", defaultWidth: 170 },
    { key: "roomTemp", label: "Room Temp", align: "center", defaultWidth: 130 },
    { key: "humidity", label: "Humidity", align: "center", defaultWidth: 130 },
    { key: "ppfd", label: "PPFD", align: "center", defaultWidth: 140 },
    { key: "lux", label: "Lux", align: "center", defaultWidth: 120 },
    { key: "roomName", label: "Room Name", align: "left", defaultWidth: 160 },
    { key: "orgCode", label: "Organization", align: "left", defaultWidth: 160, adminOnly: true },
    { key: "status", label: "Status", align: "center", defaultWidth: 130 },
    { key: "actions", label: "Actions", align: "center", defaultWidth: 280 },
  ];

  const getInitialWidths = (storageKey, columns) => {
    const defaults = {};
    columns.forEach((col) => {
      defaults[col.key] = col.defaultWidth;
    });
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return defaults;
      const parsed = JSON.parse(saved);
      if (!parsed || typeof parsed !== "object") return defaults;
      columns.forEach((col) => {
        const value = Number(parsed[col.key]);
        if (!Number.isNaN(value) && value >= MIN_COL_WIDTH) {
          defaults[col.key] = value;
        }
      });
      return defaults;
    } catch (err) {
      return defaults;
    }
  };

  const [roomName, setRoomName] = useState({});
  const [roomNames, setRoomNames] = useState([]);
  const [openSnack, setOpenSnack] = useState(false);
  const [noData, setNoData] = useState(false);
  const [openSnackErrMsg, setopenSnackErrMsg] = useState("");
  const [devices, setDevices] = useState([]);
  const [open, setOpen] = useState(false);
  const [openDeviceAdd, setOpenDeviceAdd] = useState(false);
  const [deviceIdText, setDeviceIdText] = useState("");
  const [noRoomData, setNoRoomData] = useState(false);
  const [isNewRoom, setIsNewRoom] = useState(false);
  const [orgCode, setOrgCode] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [roomNameText, setRoomNameText] = useState("");
  const [deviceIdAct, setDeviceIdAct] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userOrgCode, setUserOrgCode] = useState("");
  const [searchOrgText, setSearchOrgText] = useState("");
  const [searchDeviceText, setSearchDeviceText] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [lightColWidths, setLightColWidths] = useState(() =>
    getInitialWidths(LIGHTS_COL_WIDTH_KEY, lightColumns)
  );
  const [sensorColWidths, setSensorColWidths] = useState(() =>
    getInitialWidths(SENSOR_COL_WIDTH_KEY, sensorColumns)
  );
  const resizeRef = useRef({
    tableType: null,
    columnKey: "",
    startX: 0,
    startWidth: 0,
  });

  const theme = useTheme();
  const isCompactActions = useMediaQuery(theme.breakpoints.down("md"));

  const filteredData = devices.filter((row) => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    return (
      row.deviceName.toLowerCase().includes(lowerCaseQuery) ||
      row.deviceSerialNo.toLowerCase().includes(lowerCaseQuery)
    );
  });
  const sensorDevices = filteredData.filter(
    (row) => (row.deviceType || "").toLowerCase() === "sensor"
  );
  const otherDevices = filteredData.filter(
    (row) => (row.deviceType || "").toLowerCase() !== "sensor"
  );

  useEffect(() => {
    if (localStorage.getItem("token")) {
      const decoded = jwtDecode(localStorage.getItem("token"));
      const orgCodeUser = decoded.orgCode;
      setIsAdmin(orgCodeUser == "SUPER_ADMIN" ? true : false);
      setUserOrgCode(orgCodeUser || "");
      console.log(
        "roles: " + JSON.stringify(decoded)
      );
    }

    getDevices()
      .then(function (response) {
        getRooms()
          .then(function (response) {
            setRoomNames(response.data.rooms);
            setRoomName(response.data.rooms[0]);
          })
          .catch((error) => {
            console.log("service-error", error);
            if (!error.response) {
              setopenSnackErrMsg("Network error please try again");
              setOpenSnack(true);
            } else {
              setopenSnackErrMsg(
                error.response.data.code + " - Please try again later"
              );
              setOpenSnack(true);
            }
          });

        const actieDeviceList = [];
        const OFFLINE_THRESHOLD_SEC = 50;
        if (response.data.devices.length > 0) {
          const now = dayjs();
          response.data.devices.forEach((device) => {
            const lastOnline = dayjs(device.updatedAt);
            const diffInSeconds = now.diff(lastOnline, "second");

            if (diffInSeconds >= OFFLINE_THRESHOLD_SEC) {
              device.deviceStatus = 0;
            }
            actieDeviceList.push(device);
            if (diffInSeconds < 60) {
              device.lastOnline = `${diffInSeconds} sec ago.`;
            } else if (diffInSeconds < 3600) {
              device.lastOnline = `${Math.floor(diffInSeconds / 60)} min ago.`;
            } else if (diffInSeconds < 86400) {
              device.lastOnline = `${Math.floor(
                diffInSeconds / 3600
              )} hrs ago.`;
            } else {
              device.lastOnline = `${Math.floor(
                diffInSeconds / 86400
              )} days ago.`;
            }
          });
        }

        setDevices(response.data.devices);
      })
      .catch((error) => {
        console.log("service-error", error);
        if (!error.response) {
          setopenSnackErrMsg("Network error please try again");
          setOpenSnack(true);
          setNoData(true);
        } else {
          setopenSnackErrMsg(
            error.response.data.code + " - Please try again later"
          );
          setOpenSnack(true);
          setNoData(true);
        }
      });
  }, []);

  const handleDropdownChange = (event) => {
    // setRoomName(event.target.value);
    getDevices()
      .then(function (response) {
        setRoomName(event.target.value);
        setDevices(response.data.spectrums);
      })
      .catch((error) => {
        console.log("service-error", error);
        if (!error.response) {
          setopenSnackErrMsg("Network error please try again");
          setOpenSnack(true);
          setNoData(true);
        } else {
          setopenSnackErrMsg(
            error.response.data.code + " - Please try again later"
          );
          setOpenSnack(true);
          setNoData(true);
        }
      });
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnack(false);
  };

  const handleClose = () => {
    setOpen(!open);
    setDeviceIdText("");
    setDeviceIdAct(0);
  };

  const handleOpenNewRoom = (event) => {
    setOpen(true);
    setIsNewRoom(false);
    console.log("rooms: " + roomNames);
    console.log("device-id: " + event);
    setDeviceIdText(event.deviceSerialNo);
    setDeviceIdAct(event.deviceId);
    setRoomNameText("");
  };

  const handleOpenNewDevice = () => {
    setOpenDeviceAdd(true);
  };

  const handleDeviceClose = () => {
    setDeviceIdText("");
    setDeviceName("");
    setDeviceIdAct(0);

    setOpenDeviceAdd(!openDeviceAdd);
  };

  const handleSave = () => {
    var device_data = {};
    if (isNewRoom) {
      device_data.isNewRoom = isNewRoom;
      device_data.roomName = roomNameText;
      device_data.deviceSerial = deviceIdText;
    } else {
      device_data.roomId = roomName.roomId;
      device_data.deviceSerial = deviceIdText;
    }

    addToRoom(deviceIdAct, device_data)
      .then(function (response) {
        updateDevice();
        setOpen(!open);
        setopenSnackErrMsg("Successfully Added device ");
        setOpenSnack(true);
        if (isNewRoom) {
          window.location.reload(false);
        }
      })
      .catch((error) => {
        console.log("service-error", error);
        if (!error.response) {
          setopenSnackErrMsg("Network error please try again");
          setOpenSnack(true);
          setNoData(true);
        } else {
          setopenSnackErrMsg(
            error.response.data.code + " - Please try again later"
          );
          setOpenSnack(true);
          setNoData(true);
        }
      });
  };

  const handleDeviceAdd = () => {
    const device_data = {
      deviceName: deviceName,
      // orgCode: orgCode,
      deviceSerialNo: deviceIdText,
    };
    if (deviceName != "" && deviceIdText != "") {
      createDevice(device_data)
        .then(function (response) {
          updateDevice();
          setOpenDeviceAdd(!openDeviceAdd);
          setopenSnackErrMsg("Successfully Added device " + response.data.code);
          setOpenSnack(true);
        })
        .catch((error) => {
          console.log("service-error", error);
          if (!error.response) {
            setopenSnackErrMsg("Network error please try again");
            setOpenSnack(true);
            setNoData(true);
          } else {
            setopenSnackErrMsg(
              error.response.data.code + " - Please try again later"
            );
            setOpenSnack(true);
            setNoData(true);
          }
        });
    } else {
      setopenSnackErrMsg("Please enter valid Data!");
      setOpenSnack(true);
    }
  };

  const handleRoomChange = (event) => {
    setRoomName(event.target.value);
  };

  const onDeviceNameChange = (event) => {
    setDeviceName(event.target.value);
  };

  const onDeviceIdChange = (event) => {
    setDeviceIdText(event.target.value);
  };

  const handleRoomNameText = (event) => {
    setRoomNameText(event.target.value);
  };

  const handleSearchByDevice = (event) => {
    setSearchDeviceText(event.target.value);
    const parms = {
      deviceName: event.target.value,
    };
    getDevicesFilter(parms)
      .then(function (response) {
        setDevices(response.data.devices);
      })
      .catch((error) => {
        console.log("service-error", error);
        if (!error.response) {
          setopenSnackErrMsg("Network error please try again");
          setOpenSnack(true);
          setNoData(true);
        } else {
          setopenSnackErrMsg(
            error.response.data.code + " - Please try again later"
          );
          setOpenSnack(true);
          setNoData(true);
        }
      });
  };

  const handleSearchByOrg = (event) => {
    setSearchOrgText(event.target.value);
    const parms = {
      orgCode: searchOrgText,
    };
    getDevicesFilter(parms)
      .then(function (response) {
        setDevices(response.data.devices);
      })
      .catch((error) => {
        console.log("service-error", error);
        if (!error.response) {
          setopenSnackErrMsg("Network error please try again");
          setOpenSnack(true);
          setNoData(true);
        } else {
          setopenSnackErrMsg(
            error.response.data.code + " - Please try again later"
          );
          setOpenSnack(true);
          setNoData(true);
        }
      });
  };

  const handleClickOpenDelete = (row) => {
    setOpenDelete(true);
    setDeleteId(row.deviceId);
  };

  const handleCloseDeletePopup = () => {
    setOpenDelete(false);
    setDeleteId("");
  };

  const handleConfirmedDelete = (row) => {
    deleteDevice(deleteId)
      .then(function (response) {
        setopenSnackErrMsg("Successfully deleted user");
        setOpenSnack(true);
        updateDevice();
        setOpenDelete(false);
        setDeleteId("");
      })
      .catch((error) => {
        console.log("service-error", error);
        if (!error.response) {
          setopenSnackErrMsg("Network error please try again");
          setOpenSnack(true);
        } else {
          setopenSnackErrMsg(
            error.response.data.code + " - Please try again later"
          );
          setOpenSnack(true);
        }
      });
  };

  const updateDevice = () => {
    getDevices()
      .then(function (response) {
        const actieDeviceList = [];
        const OFFLINE_THRESHOLD_SEC = 50;
        if (response.data.devices.length > 0) {
          const now = dayjs();
          response.data.devices.forEach((device) => {
            const lastOnline = dayjs(device.updatedAt);
            const diffInSeconds = now.diff(lastOnline, "second");

            if (diffInSeconds >= OFFLINE_THRESHOLD_SEC) {
              device.deviceStatus = 0;
            }
            actieDeviceList.push(device);
            if (diffInSeconds < 60) {
              device.lastOnline = `${diffInSeconds} sec ago.`;
            } else if (diffInSeconds < 3600) {
              device.lastOnline = `${Math.floor(diffInSeconds / 60)} min ago.`;
            } else if (diffInSeconds < 86400) {
              device.lastOnline = `${Math.floor(
                diffInSeconds / 3600
              )} hrs ago.`;
            } else {
              device.lastOnline = `${Math.floor(
                diffInSeconds / 86400
              )} days ago.`;
            }
          });
        }

        setDevices(response.data.devices);
      })
      .catch((error) => {
        console.log("service-error", error);
        if (!error.response) {
          setopenSnackErrMsg("Network error please try again");
          setOpenSnack(true);
          setNoData(true);
        } else {
          setopenSnackErrMsg(
            error.response.data.code + " - Please try again later"
          );
          setOpenSnack(true);
          setNoData(true);
        }
      });
  };

  const handleBlink = (row) => {
    console.log("rooms: " + row.deviceSerialNo);
    deviceBlink(row.deviceSerialNo)
      .then(function (response) {
        setopenSnackErrMsg("Blinking device " + row.deviceSerialNo);
        setOpenSnack(true);
      })
      .catch((error) => {
        console.log("service-error", error);
        if (!error.response) {
          setopenSnackErrMsg("Network error please try again");
          setOpenSnack(true);
        } else {
          setopenSnackErrMsg(
            error.response.data.code + " - Please try again later"
          );
          setOpenSnack(true);
        }
      });
  };

  const handleSearchChange = (event) => {
    console.log(event.target.value);
    setSearchQuery(event.target.value);
  };

  // const columns = [
  //   { field: "deviceName", headerName: "Device Name", width: 70 },
  //   { field: "deviceId", headerName: "Device ID", width: 130 },
  //   { field: "lastName", headerName: "Device Temperature", width: 130 },
  //   { field: "lastName", headerName: "Fan Speed", width: 90 },
  //   { field: "lastName", headerName: "Room Name", width: 90 },
  //   { field: "lastName", headerName: "Organization", width: 90 },
  //   {
  //     field: "lastName",
  //     headerName: "Device Status",
  //     width: 90,
  //     sortable: false,
  //   },
  //   { field: "lastName", headerName: "Actions", width: 90, sortable: false },
  // ];

  // const rows = [{ id: 1, lastName: "Snow", firstName: "Jon", age: 35 }];

  const snackAction = (
    <Fragment>
      {/* <Button color="secondary" size="small" onClick={handleCloseSnack}>
        UNDO
      </Button> */}
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleCloseSnack}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Fragment>
  );

  const showNewRoom = () => {
    setIsNewRoom(!isNewRoom);
  };

  const isSensorParamNA = (row) => {
    if ((row.deviceType || "").toLowerCase() !== "sensor") return false;
    return isSensorTelemetryStale(row);
  };

  useEffect(() => {
    localStorage.setItem(LIGHTS_COL_WIDTH_KEY, JSON.stringify(lightColWidths));
  }, [lightColWidths]);

  useEffect(() => {
    localStorage.setItem(SENSOR_COL_WIDTH_KEY, JSON.stringify(sensorColWidths));
  }, [sensorColWidths]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const { tableType, columnKey, startX, startWidth } = resizeRef.current;
      if (!tableType || !columnKey) return;
      const delta = event.clientX - startX;
      const nextWidth = Math.max(MIN_COL_WIDTH, startWidth + delta);
      if (tableType === "lights") {
        setLightColWidths((prev) => ({ ...prev, [columnKey]: nextWidth }));
      } else {
        setSensorColWidths((prev) => ({ ...prev, [columnKey]: nextWidth }));
      }
    };

    const handleMouseUp = () => {
      resizeRef.current = {
        tableType: null,
        columnKey: "",
        startX: 0,
        startWidth: 0,
      };
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(updateDevice, 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  const startResize = (tableType, columnKey, event) => {
    event.preventDefault();
    event.stopPropagation();
    const widths = tableType === "lights" ? lightColWidths : sensorColWidths;
    resizeRef.current = {
      tableType,
      columnKey,
      startX: event.clientX,
      startWidth: Number(widths[columnKey]) || MIN_COL_WIDTH,
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const getColumnSx = (width) => ({
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  });

  return (
    <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Typography
          sx={{
            fontWeight: "bold",
            color: "text.primary",
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
          }}
          variant="h5"
          component="div"
        >
          Device Manager
        </Typography>
        {!isAdmin && userOrgCode && (
          <Paper elevation={1} sx={{ px: 1.5, py: 0.75, borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Organization</Typography>
            <Typography variant="body2" fontWeight={600}>{userOrgCode}</Typography>
          </Paper>
        )}
      </Stack>
      <Snackbar
        open={openSnack}
        autoHideDuration={2000}
        onClose={handleCloseSnack}
        message={openSnackErrMsg}
        action={snackAction}
      />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={3}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        flexWrap="wrap"
        useFlexGap
      >
        <Button
          variant="contained"
          onClick={handleOpenNewDevice}
          sx={{ minWidth: { xs: "100%", sm: "auto" } }}
        >
          <AddIcon />
          ADD DEVICE
        </Button>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="flex-end"
          alignItems="stretch"
          sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 0 }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={updateDevice}
            startIcon={<RefreshIcon />}
            sx={{
              px: 1.5,
              minWidth: 108,
              "& .MuiButton-startIcon": { mr: 0.75 },
            }}
          >
            Refresh
          </Button>
          {isAdmin && (
            <TextField
              margin="dense"
              size="small"
              id="search_org"
              label="Search By Organization"
              type="search"
              fullWidth
              sx={{ minWidth: { xs: 0, sm: 180 } }}
              onChange={handleSearchByOrg}
              variant="outlined"
            />
          )}
          <TextField
            margin="dense"
            size="small"
            id="search_device"
            label="Search By Device"
            type="search"
            fullWidth
            sx={{ minWidth: { xs: 0, sm: 180 } }}
            value={searchQuery}
            onChange={handleSearchChange}
            variant="outlined"
          />
        </Stack>
      </Stack>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 1 } }}>
        <DialogTitle sx={{ pr: 6 }}>Choose Room</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box sx={{ width: "100%" }}>
            <TextField
              autoFocus
              margin="dense"
              size="small"
              id="device_id"
              label="Device ID"
              type="device_id"
              fullWidth
              value={deviceIdText}
              variant="outlined"
            />
          </Box>
          {!isNewRoom && (
            <div>
              <FormControl fullWidth sx={{ minWidth: 0, mt: 2 }}>
                <InputLabel id="demo-simple-select-label">
                  Choose Room
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={roomName}
                  label="Choose Room"
                  size="small"
                  onChange={handleRoomChange}
                >
                  {noRoomData ? (
                    <em>
                      <MenuItem value="NaNa">No Data Found</MenuItem>
                    </em>
                  ) : (
                    roomNames.map((rooms, index) => (
                      <MenuItem value={rooms}>{rooms.roomName}</MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <Typography id="input-slider" align="center" sx={{ m: 1 }}>
                Or
              </Typography>
              <Box textAlign="center" mb={1}>
                <Button
                  variant="contained"
                  size="small"
                  position="relative"
                  align="center"
                  justifyContent="center"
                  onClick={showNewRoom}
                >
                  <AddIcon />
                  &nbsp;New Room
                </Button>
              </Box>
            </div>
          )}

          {isNewRoom && (
            <div>
              <Box sx={{ width: "100%", mt: 2 }}>
                <TextField
                  autoFocus
                  margin="dense"
                  size="small"
                  id="room_name"
                  label="Room Name"
                  type="room_name"
                  fullWidth
                  value={roomNameText}
                  onChange={handleRoomNameText}
                  variant="outlined"
                />
              </Box>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeviceAdd} onClose={handleDeviceClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 1 } }}>
        <DialogTitle sx={{ pr: 6 }}>Add Device</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleDeviceClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box>
            <TextField
              autoFocus
              margin="dense"
              size="small"
              id="device_name"
              label="Device Name"
              type="device_name"
              fullWidth
              value={deviceName}
              onChange={onDeviceNameChange}
              variant="outlined"
            />
          </Box>
          <Box sx={{ mt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              size="small"
              id="device_id"
              label="Device ID"
              type="device_id"
              fullWidth
              value={deviceIdText}
              onChange={onDeviceIdChange}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeviceClose}>Cancel</Button>
          <Button onClick={handleDeviceAdd}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDelete}
        onClose={handleCloseDeletePopup}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Please confirm to Delete ?
        </DialogTitle>
        <DialogContent></DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeletePopup}>No</Button>
          <Button onClick={handleConfirmedDelete}>Yes</Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h6" sx={{ mt: 2, mb: 1.5, fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 600 }}>
        Lights Devices
      </Typography>
      <TableContainer component={Paper} sx={{ width: "100%", maxWidth: "100%", borderRadius: 1, overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            width: "max-content",
            minWidth: "100%",
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              py: { xs: 0.75, sm: 1.25 },
              px: { xs: 0.75, sm: 1.5 },
              minWidth: 0,
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              fontWeight: 600,
              bgcolor: "action.hover",
              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
            },
            "& .MuiTableBody-root .MuiTableRow-root": {
              height: "48px",
              minHeight: "48px",
            },
            "& .MuiTableBody-root .MuiTableCell-root": {
              verticalAlign: "middle",
            },
          }}
          aria-label="non-sensor devices table"
        >
          <TableHead>
            <TableRow>
              {lightColumns
                .filter((col) => !col.adminOnly || isAdmin)
                .map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    sx={{
                      ...getColumnSx(lightColWidths[col.key]),
                      position: "relative",
                      px: col.key === "actions" ? 1 : undefined,
                      pr: col.key === "actions" ? 2.5 : undefined,
                    }}
                  >
                    {col.label}
                    <Box
                      onMouseDown={(event) => startResize("lights", col.key, event)}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: -4,
                        width: 8,
                        height: "100%",
                        cursor: "col-resize",
                        zIndex: 2,
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: "18%",
                          bottom: "18%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "1px",
                          bgcolor: "divider",
                        },
                        "&:hover": { bgcolor: "action.selected" },
                        "&:hover::before": {
                          width: "2px",
                          bgcolor: "primary.main",
                        },
                      }}
                    />
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {otherDevices.map((row) => (
              <TableRow
                key={row.deviceName}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row" sx={{ ...getColumnSx(lightColWidths.deviceName), fontWeight: 500 }} title={row.deviceName}>
                  {row.deviceName}
                </TableCell>
                <TableCell align="left" sx={getColumnSx(lightColWidths.deviceId)} title={row.deviceSerialNo}>
                  {row.deviceSerialNo}
                </TableCell>
                <TableCell align="center" sx={getColumnSx(lightColWidths.deviceTemperature)}>
                  {row.deviceTemperature < -5
                    ? "NA"
                    : row.deviceTemperature + " °C"}
                </TableCell>
                <TableCell align="center" sx={getColumnSx(lightColWidths.fanSpeed)}>
                  {row.fanSpeed} %
                </TableCell>
                <TableCell align="left" sx={getColumnSx(lightColWidths.roomName)} title={row.room ? row.room.roomName : "Not Added"}>
                  {row.room ? row.room.roomName : "Not Added"}
                </TableCell>
                {isAdmin && (
                  <TableCell align="left" sx={getColumnSx(lightColWidths.orgCode)} title={row.orgCode}>
                    {row.orgCode}
                  </TableCell>
                )}
                <TableCell align="center" sx={getColumnSx(lightColWidths.deviceStatus)}>
                  {row.deviceStatus == 1 ? (
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        bgcolor: "success.light",
                        "&:hover": { bgcolor: "success.light" },
                        minWidth: { xs: 56, sm: 72 },
                        fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                      }}
                    >
                      ONLINE
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        bgcolor: "grey",
                        "&:hover": { bgcolor: "grey" },
                        minWidth: { xs: 56, sm: 72 },
                        fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                      }}
                    >
                      OFFLINE
                    </Button>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ ...getColumnSx(lightColWidths.actions), pr: 2.5 }}>
                  {isCompactActions ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
                      <Tooltip title={row.room ? "Change Room" : "Add to Room"}>
                        <IconButton size="small" onClick={() => handleOpenNewRoom(row)} aria-label={row.room ? "Change Room" : "Add to Room"}>
                          {row.room ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Blink">
                        <IconButton size="small" onClick={() => handleBlink(row)} aria-label="Blink">
                          <FlashOnIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleClickOpenDelete(row)} aria-label="delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
                      {row.room ? (
                        <Button variant="outlined" size="small" sx={{ fontSize: 11, px: 1, py: 0.5 }} startIcon={<EditIcon />} onClick={() => handleOpenNewRoom(row)}>
                          Change Room
                        </Button>
                      ) : (
                        <Button variant="outlined" size="small" sx={{ fontSize: 11, px: 1, py: 0.5 }} startIcon={<AddIcon />} onClick={() => handleOpenNewRoom(row)}>
                          Add to Room
                        </Button>
                      )}
                      <Button variant="contained" size="small" sx={{ py: 0.5 }} onClick={() => handleBlink(row)}>
                        BLINK
                      </Button>
                      <IconButton size="small" aria-label="delete" onClick={() => handleClickOpenDelete(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mt: 4, mb: 1.5, fontSize: { xs: "1rem", sm: "1.25rem" }, fontWeight: 600 }}>
        Sensor Devices
      </Typography>
      <TableContainer component={Paper} sx={{ width: "100%", maxWidth: "100%", borderRadius: 1, overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            width: "max-content",
            minWidth: "100%",
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              py: { xs: 0.75, sm: 1.25 },
              px: { xs: 0.75, sm: 1.5 },
              minWidth: 0,
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              fontWeight: 600,
              bgcolor: "action.hover",
              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
            },
            "& .MuiTableBody-root .MuiTableRow-root": {
              height: "48px",
              minHeight: "48px",
            },
            "& .MuiTableBody-root .MuiTableCell-root": {
              verticalAlign: "middle",
            },
          }}
          aria-label="sensor devices table"
        >
          <TableHead>
            <TableRow>
              {sensorColumns
                .filter((col) => !col.adminOnly || isAdmin)
                .map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    sx={{
                      ...getColumnSx(sensorColWidths[col.key]),
                      position: "relative",
                      px: col.key === "actions" ? 1 : undefined,
                      pr: col.key === "actions" ? 2.5 : undefined,
                    }}
                  >
                    {col.label}
                    <Box
                      onMouseDown={(event) => startResize("sensor", col.key, event)}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: -4,
                        width: 8,
                        height: "100%",
                        cursor: "col-resize",
                        zIndex: 2,
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: "18%",
                          bottom: "18%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "1px",
                          bgcolor: "divider",
                        },
                        "&:hover": { bgcolor: "action.selected" },
                        "&:hover::before": {
                          width: "2px",
                          bgcolor: "primary.main",
                        },
                      }}
                    />
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sensorDevices.map((row) => (
              <TableRow
                key={row.deviceName}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row" sx={{ ...getColumnSx(sensorColWidths.deviceName), fontWeight: 500 }} title={row.deviceName}>
                  {row.deviceName}
                </TableCell>
                <TableCell align="left" sx={getColumnSx(sensorColWidths.deviceId)} title={row.deviceSerialNo}>
                  {row.deviceSerialNo}
                </TableCell>
                <TableCell align="center" sx={getColumnSx(sensorColWidths.roomTemp)}>
                  {isSensorParamNA(row)
                    ? "NA"
                    : (row.roomTemperature < -5 ? "NA" : row.roomTemperature + " °C")}
                </TableCell>
                <TableCell align="center" sx={getColumnSx(sensorColWidths.humidity)}>
                  {isSensorParamNA(row)
                    ? "NA"
                    : row.roomHumidity == null || row.roomHumidity === ""
                      ? "NA"
                      : (() => {
                          const h = Number(row.roomHumidity);
                          return Number.isFinite(h)
                            ? `${Math.round(h)} %`
                            : "NA";
                        })()}
                </TableCell>
                <TableCell align="center" sx={getColumnSx(sensorColWidths.ppfd)}>
                  {isSensorParamNA(row) ? "NA" : (row.ppfd == null ? "NA" : row.ppfd + " μmol/m2/s")}
                </TableCell>
                <TableCell align="center" sx={getColumnSx(sensorColWidths.lux)}>
                  {isSensorParamNA(row) ? "NA" : (row.lux == null ? "NA" : row.lux + " lux")}
                </TableCell>
                <TableCell align="left" sx={getColumnSx(sensorColWidths.roomName)} title={row.room ? row.room.roomName : "Not Added"}>
                  {row.room ? row.room.roomName : "Not Added"}
                </TableCell>
                {isAdmin && (
                  <TableCell align="left" sx={getColumnSx(sensorColWidths.orgCode)} title={row.orgCode}>
                    {row.orgCode}
                  </TableCell>
                )}
                <TableCell align="center" sx={getColumnSx(sensorColWidths.status)}>
                  {row.deviceStatus == 1 ? (
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        bgcolor: "success.light",
                        "&:hover": { bgcolor: "success.light" },
                        minWidth: { xs: 56, sm: 72 },
                        fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                      }}
                    >
                      ONLINE
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        bgcolor: "grey",
                        "&:hover": { bgcolor: "grey" },
                        minWidth: { xs: 56, sm: 72 },
                        fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                      }}
                    >
                      OFFLINE
                    </Button>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ ...getColumnSx(sensorColWidths.actions), pr: 2.5 }}>
                  {isCompactActions ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
                      <Tooltip title={row.room ? "Change Room" : "Add to Room"}>
                        <IconButton size="small" onClick={() => handleOpenNewRoom(row)} aria-label={row.room ? "Change Room" : "Add to Room"}>
                          {row.room ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Blink">
                        <IconButton size="small" onClick={() => handleBlink(row)} aria-label="Blink">
                          <FlashOnIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleClickOpenDelete(row)} aria-label="delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" flexWrap="wrap" useFlexGap>
                      {row.room ? (
                        <Button variant="outlined" size="small" sx={{ fontSize: 11, px: 1, py: 0.5 }} startIcon={<EditIcon />} onClick={() => handleOpenNewRoom(row)}>
                          Change Room
                        </Button>
                      ) : (
                        <Button variant="outlined" size="small" sx={{ fontSize: 11, px: 1, py: 0.5 }} startIcon={<AddIcon />} onClick={() => handleOpenNewRoom(row)}>
                          Add to Room
                        </Button>
                      )}
                      <Button variant="contained" size="small" sx={{ py: 0.5 }} onClick={() => handleBlink(row)}>
                        BLINK
                      </Button>
                      <IconButton size="small" aria-label="delete" onClick={() => handleClickOpenDelete(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
